// src/services/vuelosGuyanaService.js
// Capa de datos de la sección "Vuelos Guyana": lectura pública de rutas con
// fechas reales (usada por el cliente logueado, la versión pública sin
// login y el panel admin) más los RPCs de escritura -- todos con
// SECURITY DEFINER y verificación de email admin en el servidor, nunca
// updates/inserts directos desde el cliente (mismo patrón que el resto del
// proyecto: claim_referral_bonus, marcar_referido_vuelo_pagado, etc.).
import { supabase } from './supabase';

export const PROCESAR_VUELOS_URL = '/.netlify/functions/procesar-vuelos';
export const CUPOS_URGENCIA = 3;

// Las 7 categorías de destino fijas de la sección. 'miami_cuba' es la única
// de dirección inversa (ciudad_salida='Miami', destino=ciudad cubana) --
// por eso el flujo de cliente la trata distinto (sin selector de ciudad).
// 'copa' es la única con set de monedas propio (ver CATEGORIAS_SIN_ZELLE) y
// con precio doble por equipaje (precio_zelle_facturado).
export const DESTINO_CATEGORIAS = [
  { id: 'guyana',                label: 'Guyana',              icon: '🌴' },
  { id: 'suriname',               label: 'Suriname',            icon: '🏖️' },
  { id: 'mexico',                 label: 'México',              icon: '🌵' },
  { id: 'miami_cuba',             label: 'Miami → Cuba',        icon: '🌆' },
  { id: 'republica_dominicana',   label: 'Rep. Dominicana',     icon: '🏝️' },
  { id: 'san_cristobal',          label: 'San Cristóbal',       icon: '⛵' },
  { id: 'copa',                   label: 'Brasil',              icon: '🛫' },
];

// Copa no muestra Zelle ni USD Cuba -- en su lugar, Transferencia bancaria
// USD (mismo número que precio_zelle, solo cambia la etiqueta) + MXN + EUR +
// Reales. Toda otra categoría sigue mostrando el set de siempre.
export const CATEGORIAS_SIN_ZELLE = ['copa'];

// Heurística SOLO para prellenar el dropdown editable del admin al procesar
// un mensaje -- nunca se usa para filtrar/clasificar en el cliente, ahí
// siempre se lee destino_categoria ya confirmado y guardado en la fila.
export const guessDestinoCategoria = (ciudadSalida, destino) => {
  const c = (ciudadSalida || '').toLowerCase();
  const d = (destino || '').toLowerCase();
  if (c.includes('miami')) return 'miami_cuba';
  if (d.includes('guyana')) return 'guyana';
  if (d.includes('suriname') || d.includes('surinam')) return 'suriname';
  if (['cancun', 'cancún', 'merida', 'mérida', 'mexico', 'méxico', 'cdmx'].some((k) => d.includes(k))) return 'mexico';
  if (['punta cana', 'santo domingo', 'republica dominicana', 'república dominicana', 'rep. dominicana'].some((k) => d.includes(k))) return 'republica_dominicana';
  if (['san cristobal', 'san cristóbal', 'skb', 'st kitts', 'saint kitts'].some((k) => d.includes(k))) return 'san_cristobal';
  return 'guyana';
};

// ── Normalización de texto libre (mensajes pegados nunca vienen consistentes) ──
// Sin esto, "Holguín" vs "Holguin" o "ARUBA" vs "ARUBA Airlines " (espacio al
// final) se guardan como valores distintos: la detección de duplicados no los
// reconoce como la misma ruta y el selector de ciudad del cliente los muestra
// como dos ciudades separadas. Confirmado con datos reales ya publicados.
const DIACRITICS_RE = new RegExp('[' + String.fromCharCode(0x0300) + '-' + String.fromCharCode(0x036f) + ']', 'g');
const stripAccents = (s) => (s || '').normalize('NFD').replace(DIACRITICS_RE, '');
// Exportado también para el buscador del admin (filtrar rutas por
// aerolínea/ciudad sin que acentos o mayúsculas distintas rompan el match).
export const normalizeKey = (s) => stripAccents((s || '').trim()).toLowerCase();

// Si ciudadTexto coincide (sin acentos/mayúsculas) con alguna de las
// ciudades ya configuradas para el proveedor, devuelve la grafía canónica
// de esa lista (ej. siempre "Holguín", nunca "Holguin" ni "HOLGUIN"). Si no
// hay coincidencia (ciudad nueva, o Miami para miami_cuba), devuelve el
// texto tal cual, solo recortado.
export const canonicalizarCiudad = (ciudadTexto, proveedor) => {
  const key = normalizeKey(ciudadTexto);
  const match = (proveedor?.ciudades_salida || []).find((c) => normalizeKey(c) === key);
  return match || (ciudadTexto || '').trim();
};

// ── Cálculo de precios ───────────────────────────────────────────────────
// precio_zelle ya viene fijado en vuelos_rutas al momento de publicar, así
// que estos helpers solo se usan para la PREVIEW en el admin antes de
// publicar. El precio USD-Cuba y Reales del cliente siempre se calculan al
// vuelo con el margen/tasa vigentes (nunca se guardan).
export const calcPrecioZelle = (precioBaseUsd, margenDefault) =>
  Math.round((parseFloat(precioBaseUsd) || 0) + (parseFloat(margenDefault) || 0));

export const calcPrecioInfante = (proveedor) =>
  proveedor?.infante_precio_base != null
    ? Math.round(parseFloat(proveedor.infante_precio_base) + parseFloat(proveedor.margen_usd_default))
    : null;

// Todos los precios que ve el cliente se redondean a entero -- sin
// decimales ni comas, en cualquier moneda, sea cual sea el porcentaje/tasa
// que Yoedis configure desde el panel.
// USD Cuba además se redondea a múltiplo de 5 para que quede "bonito": la
// última cifra 0-2 baja a 0, 3-6 va a 5 y 7-9 sube al siguiente 10
// (1326→1325, 1306→1305, 1244→1245, 1428→1430, 1377→1380, 1357→1360).
const redondearA5 = (n) => {
  const base = Math.floor(n / 10) * 10;
  const ult = n - base;
  return base + (ult < 3 ? 0 : ult < 7 ? 5 : 10);
};

export const calcPrecioUsdCuba = (precioZelle, porcentajeUsdCuba) =>
  redondearA5(Math.round(precioZelle + (precioZelle * (parseFloat(porcentajeUsdCuba) || 0)) / 100));

// Una oferta puntual con duración (precio_zelle_promo_horas) deja de estar
// vigente sola con el tiempo -- se chequea también en el cliente (no solo en
// el job que limpia la base) para que el precio se vea correcto al toque,
// sin depender de que ya haya corrido el job de reversión.
const promoVencida = (fechaObj) => {
  if (!fechaObj?.precio_zelle_promo_desde || fechaObj?.precio_zelle_promo_horas == null) return false;
  const horas = (Date.now() - new Date(fechaObj.precio_zelle_promo_desde).getTime()) / 3600000;
  return horas >= fechaObj.precio_zelle_promo_horas;
};

// Igual que calcPrecioUsdCuba, pero respeta el override manual de USD-Cuba
// efectivo de una oferta puntual (precio_usd_cuba_promo) mientras esté
// vigente -- para ofertas con ese precio fijado a mano en vez de calculado
// con el % del proveedor (ej. "Zelle a 1261, efectivo a 1300 fijo").
export const calcPrecioUsdCubaFecha = (fechaObj, precioZelle, porcentajeUsdCuba) => {
  if (fechaObj?.precio_usd_cuba_promo != null && !promoVencida(fechaObj)) {
    return Math.round(parseFloat(fechaObj.precio_usd_cuba_promo));
  }
  return calcPrecioUsdCuba(precioZelle, porcentajeUsdCuba);
};

export const calcPrecioReales = (precioZelle, tasaBrl) =>
  Math.round(precioZelle * (parseFloat(tasaBrl) || 0));

// EUR usa la misma convención que BRL ("EUR por 1 USD") para que la regla
// de protección sea la misma en ambos: SUBIR la tasa protege, nunca bajarla
// -- si se manejara como "1 EUR = X USD" habría que bajar X para
// protegerse, justo al revés de como ya piensa Yoedis con el real.
export const calcPrecioEur = (precioZelle, tasaEur) =>
  Math.round(precioZelle * (parseFloat(tasaEur) || 0));

// MXN, mismo criterio que BRL/EUR ("MXN por 1 USD"): SUBIR la tasa protege.
export const calcPrecioMxn = (precioZelle, tasaMxn) =>
  Math.round(precioZelle * (parseFloat(tasaMxn) || 0));

// ── Tarifas escalonadas por fecha (estilo aerolínea) ─────────────────────
// La "ancla" de una ruta es su propia fecha activa más próxima -- NUNCA
// "hoy". Así una ruta de calendario disperso (ej. InterCaribbean, que
// arranca semanas en el futuro) también reparte sus fechas entre los 3
// escalones en vez de que todas caigan en el mismo por comparar contra hoy.
// Se recalcula sola en cada lectura -- no se guarda -- así que cuando pasan
// fechas o se pegan nuevas, la ancla avanza sin que haya que tocar nada.
export const calcularAnclaRuta = (fechas) => {
  const activas = (fechas || []).filter((f) => f.cupos !== 0).map((f) => f.fecha).sort();
  return activas[0] || null;
};

const diasEntre = (fechaA, fechaB) =>
  Math.round((new Date(fechaB + 'T00:00:00') - new Date(fechaA + 'T00:00:00')) / 86400000);

// Precio Zelle real de una fecha puntual de una ruta. Orden de prioridad:
// 1) precio_zelle_promo de la fecha (el "precio propio" manual -- para las
//    promos ya comunicadas, cupos_promo vacío = fijo, sin decay). Este
//    campo nunca lo toca pegar-actualización, así que sobrevive a
//    cualquier repegado.
// 2) si la ruta no usa escalones, el precio fijo de siempre (precio_zelle).
// 3) si usa escalones, el nivel según qué tan lejos está esta fecha de la
//    primera fecha activa de la propia ruta.
export const calcPrecioZelleFecha = (ruta, fechaObj) => {
  if (fechaObj?.precio_zelle_promo != null && !promoVencida(fechaObj)) return Math.round(parseFloat(fechaObj.precio_zelle_promo));
  if (!ruta?.escalonado) return Math.round(parseFloat(ruta.precio_zelle));
  const ancla = calcularAnclaRuta(ruta.fechas);
  if (!ancla || !fechaObj?.fecha) return Math.round(parseFloat(ruta.precio_zelle));
  const dias = diasEntre(ancla, fechaObj.fecha);
  const lejos = ruta.dias_corte_lejos ?? 30;
  const cerca = ruta.dias_corte_cerca ?? 7;
  if (dias > lejos) return Math.round(parseFloat(ruta.precio_zelle_barato ?? ruta.precio_zelle));
  if (dias < cerca) return Math.round(parseFloat(ruta.precio_zelle_caro ?? ruta.precio_zelle));
  return Math.round(parseFloat(ruta.precio_zelle_medio ?? ruta.precio_zelle));
};

// Mismo criterio que calcPrecioZelleFecha pero para el precio de Niños --
// sigue el mismo escalón/ancla que el precio de adultos de esa fecha (no
// tiene override propio por fecha, solo el ladder de la ruta). Devuelve
// null si la ruta no tiene ladder de niños configurado (proveedor sin
// categoría adulto_nino_infante, o ruta no escalonada sin precio_nino).
export const calcPrecioNinoFecha = (ruta, fechaObj) => {
  if (!ruta?.escalonado || ruta.precio_nino_barato == null) {
    return ruta?.precio_nino != null ? Math.round(parseFloat(ruta.precio_nino)) : null;
  }
  const ancla = calcularAnclaRuta(ruta.fechas);
  if (!ancla || !fechaObj?.fecha) return Math.round(parseFloat(ruta.precio_nino_caro));
  const dias = diasEntre(ancla, fechaObj.fecha);
  const lejos = ruta.dias_corte_lejos ?? 30;
  const cerca = ruta.dias_corte_cerca ?? 7;
  if (dias > lejos) return Math.round(parseFloat(ruta.precio_nino_barato));
  if (dias < cerca) return Math.round(parseFloat(ruta.precio_nino_caro));
  return Math.round(parseFloat(ruta.precio_nino_medio));
};

// ── Lectura pública ──────────────────────────────────────────────────────
export const getProveedores = async () => {
  const { data, error } = await supabase.from('vuelos_proveedores').select('*').order('nombre');
  if (error) return [];
  return data;
};

export const getTasaVuelos = async () => {
  const { data, error } = await supabase
    .from('vuelos_config')
    .select('tasa_brl_vuelos')
    .eq('id', 1)
    .single();
  if (error || !data) return 5.2;
  return parseFloat(data.tasa_brl_vuelos);
};

export const getTasaEurVuelos = async () => {
  const { data, error } = await supabase
    .from('vuelos_config')
    .select('tasa_eur_vuelos')
    .eq('id', 1)
    .single();
  if (error || !data) return 0.86;
  return parseFloat(data.tasa_eur_vuelos);
};

export const getTasaMxnVuelos = async () => {
  const { data, error } = await supabase
    .from('vuelos_config')
    .select('tasa_mxn_vuelos')
    .eq('id', 1)
    .single();
  if (error || !data) return 17.5;
  return parseFloat(data.tasa_mxn_vuelos);
};

// Cuenta rutas activas por categoría de destino -- siempre devuelve las 4
// categorías (incluso con count 0) para que ninguna quede oculta, mismo
// criterio que "nunca desaparece, muestra estado vacío" ya usado a nivel
// de ruta individual sin fechas vigentes.
export const getDestinoCounts = async () => {
  const { data, error } = await supabase
    .from('vuelos_rutas')
    .select('destino_categoria')
    .eq('activa', true);
  const counts = {};
  (data || []).forEach((r) => { counts[r.destino_categoria] = (counts[r.destino_categoria] || 0) + 1; });
  return DESTINO_CATEGORIAS.map((cat) => ({ ...cat, count: error ? 0 : (counts[cat.id] || 0) }));
};

// Devuelve {ciudad, label} por cada ciudad de salida de la categoría --
// normalmente label === ciudad, salvo cuando la ciudad de salida coincide
// con el nombre de la propia categoría (ej. el tramo de vuelta
// Guyana->Habana dentro de la categoría 'guyana'): ahí "Guyana" a secas
// como botón confunde porque ya se está dentro de la sección "Guyana", así
// que se muestra "Guyana - Habana" para dejar claro hacia dónde va.
export const getCiudadesSalidaPorDestino = async (categoria) => {
  const { data, error } = await supabase
    .from('vuelos_rutas')
    .select('ciudad_salida, destino')
    .eq('activa', true)
    .eq('destino_categoria', categoria);
  if (error || !data) return [];
  const destinoPorCiudad = new Map();
  data.forEach((r) => destinoPorCiudad.set(r.ciudad_salida, r.destino));
  const categoriaLabel = DESTINO_CATEGORIAS.find((c) => c.id === categoria)?.label || '';
  return [...destinoPorCiudad.keys()].sort().map((ciudad) => ({
    ciudad,
    label: normalizeKey(ciudad) === normalizeKey(categoriaLabel)
      ? `${ciudad} - ${destinoPorCiudad.get(ciudad)}`
      : ciudad,
  }));
};

const mapRutaConFechas = (r) => {
  const hoy = new Date().toISOString().slice(0, 10);
  return {
    ...r,
    porcentaje_usd_cuba: r.vuelos_proveedores?.porcentaje_usd_cuba ?? 0,
    categorias: r.vuelos_proveedores?.categorias ?? 'adulto_infante',
    fechas: (r.vuelos_fechas || [])
      .filter((f) => f.fecha >= hoy && f.cupos !== 0)
      .sort((a, b) => a.fecha.localeCompare(b.fecha)),
  };
};

// Rutas activas de una ciudad + categoría de destino, con sus fechas y el
// % USD-Cuba/categoría del proveedor (necesarios para calcular precios y el
// campo Niños en el modal de reserva), sin exponer nunca el nombre del
// proveedor al cliente.
export const getRutasByCiudadYDestino = async (ciudad, categoria) => {
  const { data, error } = await supabase
    .from('vuelos_rutas')
    .select('*, vuelos_fechas(*), vuelos_proveedores(porcentaje_usd_cuba, categorias)')
    .eq('ciudad_salida', ciudad)
    .eq('destino_categoria', categoria)
    .eq('activa', true)
    .order('created_at');
  if (error || !data) return [];
  return data.map(mapRutaConFechas);
};

// Miami → Cuba no pasa por selector de ciudad (la ciudad de salida es
// siempre Miami) -- lista directa de todas las rutas activas de la categoría.
export const getRutasPorCategoria = async (categoria) => {
  const { data, error } = await supabase
    .from('vuelos_rutas')
    .select('*, vuelos_fechas(*), vuelos_proveedores(porcentaje_usd_cuba, categorias)')
    .eq('destino_categoria', categoria)
    .eq('activa', true)
    .order('created_at');
  if (error || !data) return [];
  return data.map(mapRutaConFechas);
};

// Agrupa fechas por mes para mostrarlas como chips (ej. "Julio 2026").
export const agruparFechasPorMes = (fechas) => {
  const grupos = {};
  fechas.forEach((f) => {
    const [y, m] = f.fecha.split('-');
    const key = `${y}-${m}`;
    if (!grupos[key]) grupos[key] = [];
    grupos[key].push(f);
  });
  return Object.entries(grupos).sort(([a], [b]) => a.localeCompare(b));
};

export const formatMesLabel = (key) => {
  const [y, m] = key.split('-').map(Number);
  const label = new Date(y, m - 1, 1).toLocaleDateString('es', { month: 'long', year: 'numeric' });
  return label.charAt(0).toUpperCase() + label.slice(1);
};

export const formatFechaChip = (fechaStr) => {
  const [, m, d] = fechaStr.split('-').map(Number);
  const dias = new Date(fechaStr + 'T00:00:00').toLocaleDateString('es', { weekday: 'short' });
  return `${d}`.padStart(2, '0') + ` (${dias.replace('.', '')})`;
};

// ── Admin: rutas publicadas, agrupadas por ciudad ────────────────────────
export const getRutasAdmin = async () => {
  const { data, error } = await supabase
    .from('vuelos_rutas')
    .select('*, vuelos_fechas(*), vuelos_proveedores(nombre, categorias)')
    .order('ciudad_salida')
    .order('created_at', { ascending: false });
  if (error || !data) return [];
  return data.map((r) => ({
    ...r,
    proveedor_nombre: r.vuelos_proveedores?.nombre || '—',
    categorias: r.vuelos_proveedores?.categorias || 'adulto_infante',
    fechas: (r.vuelos_fechas || []).sort((a, b) => a.fecha.localeCompare(b.fecha)),
  }));
};

// Busca una ruta del mismo proveedor con la misma combinación ciudad_salida
// + destino + aerolinea, para ofrecer reemplazar vs. agregar como versión
// adicional antes de publicar. Busca SIN filtrar por activa -- si solo mirara rutas activas, una ruta ya
// desactivada (ej. una versión vieja que el admin apagó a mano) deja de ser
// encontrable en el próximo pegado y se crea otra copia nueva, empeorando el
// problema con cada actualización. La comparación de aerolinea se hace
// recortada y sin mayúsculas porque el texto libre nunca llega idéntico
// entre dos pegados distintos del mismo proveedor (confirmado con datos
// reales: "ARUBA" vs "ARUBA Airlines ", "SKY" vs "SKY High ").
// La IA que extrae el mensaje del proveedor no siempre pone la escala en el
// mismo campo -- a veces "Aruba" (una escala, no una aerolínea) termina en
// aerolinea, otras veces en escala, para el mismo vuelo real. Comparar solo
// aerolinea hacía que esos casos no se reconocieran como duplicados y se
// creara una ruta nueva en vez de preguntar si reemplazar. Por eso la firma
// de comparación junta aerolinea+escala en vez de mirar cada campo aislado.
const firmaAerolineaEscala = (aerolinea, escala) => normalizeKey(`${aerolinea || ''} ${escala || ''}`);

// A diferencia de buscarRutaDuplicada (que compara la firma aerolinea+escala
// para PRESELECCIONAR una reemplazable), esto trae TODAS las rutas ya
// publicadas para esa ciudad→destino, sin filtrar por firma -- porque el
// proveedor no siempre describe el mismo vuelo real con el mismo texto de
// un mensaje a otro (ej. "AVIATSA/CENTURY" un día, "Air Century" otro), y
// eso ninguna comparación de campos lo detecta de forma segura. Se usa para
// que Yoedis vea de un vistazo, ANTES de publicar, qué ya existe para esa
// ciudad→destino y decida él mismo si la ruta pegada es una de esas u otra
// nueva -- en vez de depender de un window.confirm fácil de pasar por alto.
export const getRutasExistentesParaCiudadDestino = async (proveedorId, ciudadSalida, destino) => {
  if (!ciudadSalida || !destino) return [];
  const { data, error } = await supabase
    .from('vuelos_rutas')
    .select('id, aerolinea, escala, frecuencia, precio_zelle, activa')
    .eq('proveedor_id', proveedorId)
    .eq('ciudad_salida', ciudadSalida)
    .eq('destino', destino)
    .order('activa', { ascending: false });
  if (error || !data) return [];
  return data;
};

export const buscarRutaDuplicada = async (proveedorId, ciudadSalida, destino, aerolinea, escala) => {
  const { data, error } = await supabase
    .from('vuelos_rutas')
    .select('id, precio_zelle, aerolinea, escala, activa, created_at')
    .eq('proveedor_id', proveedorId)
    .eq('ciudad_salida', ciudadSalida)
    .eq('destino', destino);
  if (error || !data) return null;
  const target = firmaAerolineaEscala(aerolinea, escala);
  const candidatas = data.filter((r) => firmaAerolineaEscala(r.aerolinea, r.escala) === target);
  if (candidatas.length === 0) return null;
  // Si por datos viejos quedara más de una candidata con la misma firma
  // (no debería, pero pasó una vez por una ruta duplicada nunca borrada del
  // todo), se prioriza la activa más reciente en vez de una al azar --
  // así nunca se resucita por error la ruta vieja en vez de la vigente.
  candidatas.sort((a, b) => (b.activa - a.activa) || (new Date(b.created_at) - new Date(a.created_at)));
  return candidatas[0];
};

// ── Netlify function: extracción del mensaje pegado ──────────────────────
export const procesarMensajeVuelos = async (proveedorId, textoMensaje) => {
  const res = await fetch(PROCESAR_VUELOS_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ proveedor_id: proveedorId, texto_mensaje: textoMensaje }),
  });
  if (!res.ok) throw new Error('No se pudo procesar el mensaje.');
  const data = await res.json();
  return data.rutas || [];
};

// ── Admin: escritura (RPC security definer) ──────────────────────────────
export const actualizarTasaVuelos = async (tasa) => {
  const { error } = await supabase.rpc('actualizar_tasa_vuelos', { p_tasa: parseFloat(tasa) });
  if (error) throw error;
};

export const actualizarTasaEurVuelos = async (tasa) => {
  const { error } = await supabase.rpc('actualizar_tasa_eur_vuelos', { p_tasa: parseFloat(tasa) });
  if (error) throw error;
};

export const actualizarTasaMxnVuelos = async (tasa) => {
  const { error } = await supabase.rpc('actualizar_tasa_mxn_vuelos', { p_tasa: parseFloat(tasa) });
  if (error) throw error;
};

export const actualizarProveedorVuelos = async (proveedorId, margen, porcentaje) => {
  const { error } = await supabase.rpc('actualizar_proveedor_vuelos', {
    p_proveedor_id: proveedorId,
    p_margen: parseFloat(margen),
    p_porcentaje: parseFloat(porcentaje),
  });
  if (error) throw error;
};

export const publicarRutaVuelo = async (ruta, reemplazarRutaId = null) => {
  const { data, error } = await supabase.rpc('publicar_ruta_vuelo', {
    p_proveedor_id: ruta.proveedor_id,
    p_ciudad_salida: ruta.ciudad_salida,
    p_destino: ruta.destino,
    p_destino_categoria: ruta.destino_categoria,
    p_aerolinea: ruta.aerolinea || null,
    p_escala: ruta.escala || null,
    p_frecuencia: ruta.frecuencia || null,
    p_precio_base_usd: parseFloat(ruta.precio_base_usd),
    p_precio_zelle: parseFloat(ruta.precio_zelle),
    p_precio_nino: ruta.precio_nino !== '' && ruta.precio_nino != null ? parseFloat(ruta.precio_nino) : null,
    p_precio_infante: ruta.precio_infante !== '' && ruta.precio_infante != null ? parseFloat(ruta.precio_infante) : null,
    p_fuente_mensaje_original: ruta.fuente_mensaje_original || null,
    p_fechas: (ruta.fechas || []).map((f) => ({ fecha: f.fecha, cupos: f.cupos ?? '' })),
    p_reemplazar_ruta_id: reemplazarRutaId,
    p_precio_zelle_facturado: ruta.precio_zelle_facturado !== '' && ruta.precio_zelle_facturado != null
      ? parseFloat(ruta.precio_zelle_facturado)
      : null,
  });
  if (error) throw error;
  // El precio de ida y vuelta se guarda aparte (publicar_ruta_vuelo no lo toca), para no tener que
  // reemplazar la función de publicar. Siempre se manda -- vacío = la ruta no tiene ese precio.
  if (data && ruta.precio_zelle_ida_vuelta !== undefined) {
    await actualizarPrecioIdaVueltaRutaVuelo(data, ruta.precio_base_ida_vuelta, ruta.precio_zelle_ida_vuelta);
  }
  return data;
};

// Precio de ida y vuelta de una ruta (precio_base_usd / precio_zelle son el de solo ida). Vacío o null
// = la ruta no tiene precio de ida y vuelta distinto.
export const actualizarPrecioIdaVueltaRutaVuelo = async (rutaId, precioBase, precioZelle) => {
  const num = (v) => (v === '' || v == null ? null : parseFloat(v));
  const { error } = await supabase.rpc('actualizar_precio_ida_vuelta_ruta_vuelo', {
    p_ruta_id: rutaId,
    p_precio_base_ida_vuelta: num(precioBase),
    p_precio_zelle_ida_vuelta: num(precioZelle),
  });
  if (error) throw error;
};

// Borra la fecha del todo (ej. ya pasó, o el proveedor dejó de ofrecerla).
// A diferencia de poner cupos en 0, esto la quita también del panel admin,
// no solo del catálogo del cliente -- para rutas que se manejan a mano sin
// repegar mensajes (como InterCaribbean).
export const eliminarFechaVuelo = async (fechaId) => {
  const { error } = await supabase.rpc('eliminar_fecha_vuelo', { p_fecha_id: fechaId });
  if (error) throw error;
};

export const actualizarCuposFechaVuelo = async (fechaId, cupos) => {
  const { error } = await supabase.rpc('actualizar_cupos_fecha_vuelo', {
    p_fecha_id: fechaId,
    p_cupos: cupos === '' || cupos === null ? null : parseInt(cupos, 10),
  });
  if (error) throw error;
};

// Fecha promocional: precio Zelle distinto al de la ruta para esa fecha
// puntual. Pasar '' o null borra la promo (la fecha vuelve al precio de la
// ruta). El cupo de esa misma fecha (ya existente) es el que limita cuántos
// asientos hay a ese precio -- no hay un campo de cupo separado para promo.
// usdCubaPromo (opcional): fija el efectivo USD-Cuba a un valor exacto en
// vez de calcularlo con el % del proveedor. horasValidez (opcional): a las
// N horas de puesta, la promo se revierte sola (precio y efectivo vuelven a
// los de la ruta) -- null = sin vencimiento, se queda hasta que se quite a mano.
export const actualizarPrecioPromoFechaVuelo = async (fechaId, precioPromo, usdCubaPromo = null, horasValidez = null) => {
  const { error } = await supabase.rpc('actualizar_precio_promo_fecha_vuelo', {
    p_fecha_id: fechaId,
    p_precio_promo: precioPromo === '' || precioPromo === null ? null : parseFloat(precioPromo),
    p_usd_cuba_promo: usdCubaPromo === '' || usdCubaPromo == null ? null : parseFloat(usdCubaPromo),
    p_horas_validez: horasValidez === '' || horasValidez == null ? null : parseInt(horasValidez, 10),
  });
  if (error) throw error;
};

// Cupos de oferta: un contador de marketing SEPARADO del cupo real de la
// fecha (que viene del proveedor). Yoedis pone el número de partida una
// vez -- no lo actualiza a mano según vaya vendiendo. Lo que ve el cliente
// (calcCuposPromoRestantes) baja solo con el tiempo hasta llegar a 0, para
// que la oferta no se vea "pegada" en el mismo número para siempre.
export const actualizarCuposPromoFechaVuelo = async (fechaId, cuposPromo, diasDuracion) => {
  const { error } = await supabase.rpc('actualizar_cupos_promo_fecha_vuelo', {
    p_fecha_id: fechaId,
    p_cupos_promo: cuposPromo === '' || cuposPromo === null ? null : parseInt(cuposPromo, 10),
    p_dias_duracion: diasDuracion === '' || diasDuracion == null ? null : parseInt(diasDuracion, 10),
  });
  if (error) throw error;
};

// Ventana de días en la que el contador de oferta baja de cupos_promo a 0.
// Ajustable -- hoy 4 días desde que se puso/tocó el precio o el cupo de
// oferta (precio_zelle_promo_desde). Con jitter leve por fecha para que
// varias promos no bajen todas exactamente igual de sincronizadas.
const VENTANA_DECAY_DIAS = 4;

const hashFechaId = (id) => {
  let h = 0;
  for (let i = 0; i < (id || '').length; i++) h = (h * 31 + id.charCodeAt(i)) >>> 0;
  return h;
};

// Cuántos cupos de oferta le quedan a esta fecha AHORA MISMO -- calculado
// con el tiempo transcurrido, sin ninguna escritura nueva. null si no hay
// contador de oferta puesto (promo sin límite mostrado).
export const calcCuposPromoRestantes = (fechaObj) => {
  if (fechaObj?.cupos_promo == null) return null;
  if (!fechaObj.precio_zelle_promo_desde) return fechaObj.cupos_promo;
  const dias = (Date.now() - new Date(fechaObj.precio_zelle_promo_desde).getTime()) / 86400000;
  // promo_dias_duracion: cuando Yoedis pone un número exacto de días en el
  // panel, se usa tal cual en vez del jitter automático (que solo cubre
  // ~3.2 a 4.76 días y no permite pedir, por ejemplo, "que se vaya en 5").
  const ventana = fechaObj.promo_dias_duracion != null
    ? fechaObj.promo_dias_duracion
    : VENTANA_DECAY_DIAS * (0.8 + (hashFechaId(fechaObj.id) % 40) / 100); // jitter 0.80 – 1.19
  return Math.max(0, Math.ceil(fechaObj.cupos_promo * (1 - dias / ventana)));
};

export const setRutaVueloActiva = async (rutaId, activa) => {
  const { error } = await supabase.rpc('set_ruta_vuelo_activa', { p_ruta_id: rutaId, p_activa: activa });
  if (error) throw error;
};

// Algunos proveedores/rutas no tienen cómo recibir el pago en USD dentro de
// Cuba (ej. Sky High desde La Habana, InterCaribbean con el proveedor
// nuevo) -- este flag por ruta oculta el precio "USD Cuba" en pantalla y en
// el mensaje de WhatsApp para esa ruta puntual, sin tocar el resto.
export const setRutaAdmiteUsdCuba = async (rutaId, admite) => {
  const { error } = await supabase.rpc('set_ruta_admite_usd_cuba', { p_ruta_id: rutaId, p_admite: admite });
  if (error) throw error;
};

// "Solo USD en Cuba": la ruta se muestra únicamente con su precio en USD (efectivo), sin Zelle, reales,
// euros ni pesos. El precio es el de Zelle de cada fecha más el % de USD Cuba del proveedor (redondeado).
export const setRutaSoloUsdCuba = async (rutaId, solo) => {
  const { error } = await supabase.rpc('set_ruta_solo_usd_cuba', { p_ruta_id: rutaId, p_solo: solo });
  if (error) throw error;
};

// Borra la ruta del todo (fechas incluidas, en cascada) -- el RPC en sí
// rechaza borrar una ruta activa (hay que apagarla primero con
// setRutaVueloActiva), para que nunca se pueda borrar por error una que un
// cliente está viendo en vivo.
export const eliminarRutaVuelo = async (rutaId) => {
  const { error } = await supabase.rpc('eliminar_ruta_vuelo', { p_ruta_id: rutaId });
  if (error) throw error;
};

export const actualizarPrecioRutaVuelo = async (rutaId, precioBaseUsd, precioZelle) => {
  const { error } = await supabase.rpc('actualizar_precio_ruta_vuelo', {
    p_ruta_id: rutaId,
    p_precio_base_usd: parseFloat(precioBaseUsd),
    p_precio_zelle: parseFloat(precioZelle),
  });
  if (error) throw error;
};

// RPC separado del de adultos (actualizarPrecioRutaVuelo) para no arriesgar
// pisar a null el precio niño/infante de otras rutas cada vez que se guarda
// solo el precio de adultos. precioBaseNino/precioBaseInfante son el costo
// que da el proveedor por edad -- solo estructura, no se traducen solos al
// precio del cliente (precioNino/precioInfante), que Yoedis carga aparte
// cuando decide cuánto cobrar.
export const actualizarPreciosEdadesRutaVuelo = async (rutaId, precioBaseNino, precioNino, precioBaseInfante, precioInfante) => {
  const num = (v) => (v === '' || v == null ? null : parseFloat(v));
  const { error } = await supabase.rpc('actualizar_precios_edades_ruta_vuelo', {
    p_ruta_id: rutaId,
    p_precio_base_nino: num(precioBaseNino),
    p_precio_nino: num(precioNino),
    p_precio_base_infante: num(precioBaseInfante),
    p_precio_infante: num(precioInfante),
  });
  if (error) throw error;
};

// Agrega (o actualiza, si ya existe) una fecha suelta a una ruta ya
// publicada, sin tener que re-pegar todo el mensaje del proveedor.
export const agregarFechaVuelo = async (rutaId, fecha, cupos) => {
  const { error } = await supabase.rpc('agregar_fecha_vuelo', {
    p_ruta_id: rutaId,
    p_fecha: fecha,
    p_cupos: cupos === '' || cupos == null ? null : parseInt(cupos, 10),
  });
  if (error) throw error;
};

// Configuración de escalones de una ruta (los 3 precios + cortes de días,
// más el ladder de niños si aplica). Se pone una sola vez por ruta -- se
// aplica solo a todas sus fechas, incluidas las que se peguen después.
export const actualizarEscalonesRutaVuelo = async (rutaId, cfg) => {
  const { error } = await supabase.rpc('actualizar_escalones_ruta_vuelo', {
    p_ruta_id: rutaId,
    p_escalonado: !!cfg.escalonado,
    p_precio_barato: cfg.precioBarato === '' || cfg.precioBarato == null ? null : parseFloat(cfg.precioBarato),
    p_precio_medio: cfg.precioMedio === '' || cfg.precioMedio == null ? null : parseFloat(cfg.precioMedio),
    p_precio_caro: cfg.precioCaro === '' || cfg.precioCaro == null ? null : parseFloat(cfg.precioCaro),
    p_dias_lejos: cfg.diasLejos === '' || cfg.diasLejos == null ? 30 : parseInt(cfg.diasLejos, 10),
    p_dias_cerca: cfg.diasCerca === '' || cfg.diasCerca == null ? 7 : parseInt(cfg.diasCerca, 10),
    p_precio_nino_barato: cfg.precioNinoBarato === '' || cfg.precioNinoBarato == null ? null : parseFloat(cfg.precioNinoBarato),
    p_precio_nino_medio: cfg.precioNinoMedio === '' || cfg.precioNinoMedio == null ? null : parseFloat(cfg.precioNinoMedio),
    p_precio_nino_caro: cfg.precioNinoCaro === '' || cfg.precioNinoCaro == null ? null : parseFloat(cfg.precioNinoCaro),
  });
  if (error) throw error;
};

export const actualizarEquipajeRutaVuelo = async (rutaId, equipaje) => {
  const { error } = await supabase.rpc('actualizar_equipaje_ruta_vuelo', {
    p_ruta_id: rutaId,
    p_equipaje: equipaje || null,
  });
  if (error) throw error;
};

// ── Analytics: visitas y navegación (sin datos personales) ──────────────
// session_id aleatorio guardado en localStorage, solo para aproximar
// visitantes únicos -- no identifica a la persona ni requiere login.
const SESSION_KEY = 'click_vuelos_session';
export const getVuelosSessionId = () => {
  let id = localStorage.getItem(SESSION_KEY);
  if (!id) {
    id = (crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`);
    localStorage.setItem(SESSION_KEY, id);
  }
  return id;
};

// Exclusión de las propias visitas del admin (para que probar el enlace
// desde su teléfono/navegador no infle las estadísticas). Es un flag por
// navegador en localStorage -- se activa una vez y queda para siempre en
// ese dispositivo hasta que se borre manualmente o se limpien los datos del
// navegador. Se activa desde el toggle en el panel admin (Stats) o abriendo
// el enlace público con ?notrack=1 una vez en cada teléfono/navegador.
const NO_TRACK_KEY = 'click_vuelos_no_track';
export const isVuelosTrackingOptedOut = () => localStorage.getItem(NO_TRACK_KEY) === '1';
export const setVuelosTrackingOptOut = (activo) => {
  if (activo) localStorage.setItem(NO_TRACK_KEY, '1');
  else localStorage.removeItem(NO_TRACK_KEY);
};

// Dispositivo/navegador detectados por user agent, sin ninguna librería --
// mismo criterio que el regex de isIOS ya usado en el proyecto.
export const detectarDeviceType = () => {
  const ua = navigator.userAgent || '';
  return /Mobi|Android|iPhone|iPad|iPod/i.test(ua) ? 'movil' : 'desktop';
};

export const detectarBrowser = () => {
  const ua = navigator.userAgent || '';
  if (/Edg\//.test(ua)) return 'Edge';
  if (/OPR\//.test(ua) || /Opera/.test(ua)) return 'Opera';
  if (/Chrome\//.test(ua) && !/Edg\//.test(ua)) return 'Chrome';
  if (/Firefox\//.test(ua)) return 'Firefox';
  if (/Safari\//.test(ua) && !/Chrome\//.test(ua)) return 'Safari';
  return 'Otro';
};

// Canal de origen de la visita. Prioridad: 1) ?src=nombre en el link (si alguna vez se usa), que se
// guarda en localStorage; 2) detección automática, sin tocar los links: la app interna de
// Facebook/Instagram/TikTok se reconoce por el user agent, y el resto (Google, Facebook de escritorio,
// etc.) por la página de la que se viene (document.referrer). Si nada de eso aplica, queda null: es
// tráfico directo o de WhatsApp (WhatsApp no manda referrer), que el panel muestra aparte.
const SRC_KEY = 'click_vuelos_src';
export const detectarOrigenAutomatico = () => {
  const ua = navigator.userAgent || '';
  if (/FBAN|FBAV|FB_IAB|FBIOS|FB4A/i.test(ua)) return 'facebook';
  if (/Instagram/i.test(ua)) return 'instagram';
  if (/musical_ly|TikTok|BytedanceWebview|Bytedance/i.test(ua)) return 'tiktok';
  const ref = (document.referrer || '').toLowerCase();
  if (!ref) return null;
  if (ref.startsWith('android-app://')) {
    if (ref.includes('com.facebook')) return 'facebook';
    if (ref.includes('com.instagram')) return 'instagram';
    if (ref.includes('com.whatsapp')) return 'whatsapp';
    if (ref.includes('com.zhiliaoapp') || ref.includes('com.ss.android')) return 'tiktok';
    if (ref.includes('com.google.android.googlequicksearchbox')) return 'google';
    return null;
  }
  let host = '';
  try { host = new URL(ref).hostname; } catch { return null; }
  if (host === window.location.hostname) return null;
  if (/(^|\.)(facebook\.com|fb\.com|fb\.me)$/.test(host)) return 'facebook';
  if (/(^|\.)instagram\.com$/.test(host)) return 'instagram';
  if (/(^|\.)tiktok\.com$/.test(host)) return 'tiktok';
  if (/(^|\.)(whatsapp\.com|wa\.me)$/.test(host)) return 'whatsapp';
  if (/(^|\.)google\./.test(host)) return 'google';
  if (/(^|\.)(youtube\.com|youtu\.be)$/.test(host)) return 'youtube';
  if (/(^|\.)(t\.co|twitter\.com|x\.com)$/.test(host)) return 'x';
  return host.replace(/^www\./, '');
};

export const getVuelosSrc = () => {
  const fromUrl = new URLSearchParams(window.location.search).get('src');
  if (fromUrl) {
    localStorage.setItem(SRC_KEY, fromUrl);
    return fromUrl;
  }
  return localStorage.getItem(SRC_KEY) || detectarOrigenAutomatico();
};

// Fire-and-forget: nunca debe interrumpir la navegación del cliente si falla.
// El builder que devuelve supabase.rpc() es un "thenable", no una Promise
// real -- no tiene .catch(), así que hay que await-earlo dentro de un
// try/catch (llamar .catch() directo sobre él revienta con
// "...catch is not a function" de forma síncrona en cuanto se invoca).
export const logVuelosEvento = (evento, { origen, destinoCategoria, ciudadSalida, destino, fecha, rutaId, aerolinea } = {}) => {
  if (isVuelosTrackingOptedOut()) return;
  (async () => {
    try {
      await supabase.rpc('registrar_evento_vuelos', {
        p_evento: evento,
        p_origen: origen,
        p_destino_categoria: destinoCategoria || null,
        p_ciudad_salida: ciudadSalida || null,
        p_destino: destino || null,
        p_fecha: fecha || null,
        p_ruta_id: rutaId || null,
        p_session_id: getVuelosSessionId(),
        p_device_type: detectarDeviceType(),
        p_browser: detectarBrowser(),
        p_src: getVuelosSrc(),
        // Canal marcado en este dispositivo (ej. 'j' = Luis Omar), para
        // poder ver en el panel las estadísticas de ese canal separadas
        // del tráfico propio del grupo. Puramente informativo, no toca
        // ningún precio.
        p_canal: getCanalActivo(),
        // Código de gestor marcado en este dispositivo, para separar sus
        // estadísticas igual que con el canal. Puramente informativo.
        p_gestor_codigo: getGestorActivoCodigo(),
        // Denormalizada en el evento (como ciudad_salida/destino) para
        // poder cruzar ruta x aerolínea en Stats sin depender de un join
        // contra vuelos_rutas.
        p_aerolinea: aerolinea || null,
      });
    } catch {}
  })();
};

// Admin: eventos crudos de los últimos N días -- la agregación (ranking de
// ciudades/fechas, visitantes únicos) se hace en el componente, mismo
// criterio que el resto del panel admin (agrupar en el cliente).
//
// Pagina con .range() porque PostgREST corta cada consulta a 1000 filas
// (tope del proyecto) -- con todos los tipos de evento mezclados (ver_fecha
// es, con diferencia, el más frecuente) ya se superan las 1000 filas en una
// ventana de 90 días. Sin paginar, ese tope se comía en silencio eventos
// más viejos -- incluidas visitas reales -- cada vez que había más
// navegación reciente, haciendo que "más gente entrando" mostrara *menos*
// visitas contadas.
const PAGE_SIZE = 1000;
// desdeDias en null/undefined = sin corte por fecha, trae el historial
// completo (paginado igual, así que sigue sin toparse en 1000 filas).
export const getVuelosEventos = async (desdeDias = 30) => {
  const todas = [];
  let desdeIndice = 0;
  while (true) {
    let query = supabase
      .from('vuelos_eventos')
      .select('*')
      .order('created_at', { ascending: false })
      .range(desdeIndice, desdeIndice + PAGE_SIZE - 1);
    if (desdeDias != null) {
      const desde = new Date();
      desde.setDate(desde.getDate() - desdeDias);
      query = query.gte('created_at', desde.toISOString());
    }
    const { data, error } = await query;
    if (error || !data) break;
    todas.push(...data);
    if (data.length < PAGE_SIZE) break;
    desdeIndice += PAGE_SIZE;
  }
  return todas;
};

// ── Canal de Luis Omar (y futuros casos iguales) ─────────────────────────
// Ya NO aplica ningún descuento de precio -- el canal solo sirve para el
// saludo del mensaje ("vengo de parte de...") y para separar sus
// estadísticas del tráfico propio (p_canal en registrar_evento_vuelos).
// El precio que ve y que se manda por WhatsApp es siempre el mismo que ve
// cualquier otra visita (normal o escalonado según la fecha).
//
// Lista fija en el cliente -- no hay tabla en BD todavía porque hoy es un
// único caso (Luis Omar). Agregar otro canal es sumar una fila acá; si en
// el futuro son varios y cambian seguido, vale la pena moverlo a
// vuelos_config con un RPC admin, como ya se hizo con la tasa de reales.
export const CANALES_DESCUENTO = {
  j: { nombre: 'Luis Omar' },
};

// Cuánto dura marcada la visita en este dispositivo desde que entra por el
// link del canal. Sin esto, alguien que vuelve meses después por otro
// camino (buscándote en Google, por ejemplo) seguiría arrastrando el
// descuento para siempre.
const CANAL_DIAS_VIGENCIA = 45;
const CANAL_KEY = 'click_vuelos_canal';
const CANAL_EXPIRA_KEY = 'click_vuelos_canal_expira';

export const setCanalActivo = (canal) => {
  localStorage.setItem(CANAL_KEY, canal);
  localStorage.setItem(CANAL_EXPIRA_KEY, String(Date.now() + CANAL_DIAS_VIGENCIA * 86400000));
};

export const limpiarCanalActivo = () => {
  localStorage.removeItem(CANAL_KEY);
  localStorage.removeItem(CANAL_EXPIRA_KEY);
};

// Devuelve el código del canal activo en este dispositivo, o null si nunca
// se marcó o si ya venció. Vencido, limpia las dos claves de una vez.
export const getCanalActivo = () => {
  const canal = localStorage.getItem(CANAL_KEY);
  if (!canal) return null;
  const expira = parseInt(localStorage.getItem(CANAL_EXPIRA_KEY) || '0', 10);
  if (Date.now() > expira) {
    limpiarCanalActivo();
    return null;
  }
  return canal;
};

// Info del canal activo (nombre + margen), o null si no hay ninguno activo,
// si ya venció, o si el código guardado ya no existe en CANALES_DESCUENTO.
//
// IMPORTANTE: el canal solo puede aplicar en el flujo PÚBLICO (origen
// 'publico'). Es una marca por dispositivo en localStorage, no por sesión
// de navegación -- sin este chequeo, alguien que entra logueado en el mismo
// teléfono/navegador que antes visitó el link del canal (ej. Yoedis
// probándolo en su propio celular) seguiría viendo el descuento en la app
// normal con su cuenta, donde nunca debería aparecer.
export const getCanalDescuentoInfo = (origen) => {
  if (origen !== 'publico') return null;
  const canal = getCanalActivo();
  return canal && CANALES_DESCUENTO[canal] ? { codigo: canal, ...CANALES_DESCUENTO[canal] } : null;
};

// ── Tema 4: gestores con cuenta propia ───────────────────────────────────
// Mismo patrón de dispositivo+vigencia que el canal de Luis Omar, pero acá
// el gestor no es una constante fija en el cliente -- lo crea el admin, así
// que hay que resolverlo contra la base de datos. Como armar el mensaje de
// WhatsApp tiene que ser síncrono (se dispara en el mismo clic del botón),
// el nombre/descuento se resuelven UNA VEZ al entrar (sincronizarGestorActivo,
// llamada desde el mount de VuelosGuyanaFlow) y quedan cacheados en
// localStorage -- construirSaludoWhatsapp y calcPrecioZelleMensaje solo
// leen ese caché, nunca hacen la consulta ellos mismos.
const GESTOR_DIAS_VIGENCIA = 45;
const GESTOR_KEY = 'click_vuelos_gestor_codigo';
const GESTOR_EXPIRA_KEY = 'click_vuelos_gestor_expira';
const GESTOR_CACHE_KEY = 'click_vuelos_gestor_cache';

// ¿Este dispositivo es del admin? Sí si tiene la sesión del admin abierta (Supabase guarda la sesión
// en localStorage; la pantalla pública se sirve sin AuthProvider, así que se lee directo) o si tiene
// activada la exclusión de visitas propias (?notrack=1). En un dispositivo así nunca se marca ni se
// respeta un gestor: no se le oculta el grupo de WhatsApp, no se aplica el descuento del gestor al
// mensaje y sus visitas de prueba no se cuentan a ningún gestor.
const ADMIN_EMAIL_DISPOSITIVO = 'reservas@clicktravelagency.com.br';
export const esDispositivoAdmin = () => {
  try {
    if (isVuelosTrackingOptedOut()) return true;
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (!k || !k.startsWith('sb-') || !k.endsWith('-auth-token')) continue;
      try {
        const sesion = JSON.parse(localStorage.getItem(k) || 'null');
        if (sesion?.user?.email === ADMIN_EMAIL_DISPOSITIVO) return true;
      } catch {}
    }
  } catch {}
  return false;
};

export const setGestorActivo = (codigo) => {
  if (esDispositivoAdmin()) { limpiarGestorActivo(); return; }
  localStorage.setItem(GESTOR_KEY, codigo);
  localStorage.setItem(GESTOR_EXPIRA_KEY, String(Date.now() + GESTOR_DIAS_VIGENCIA * 86400000));
};

export const limpiarGestorActivo = () => {
  localStorage.removeItem(GESTOR_KEY);
  localStorage.removeItem(GESTOR_EXPIRA_KEY);
  localStorage.removeItem(GESTOR_CACHE_KEY);
};

// Exportado (a diferencia del resto de los helpers de gestor, que son
// internos) porque logVuelosEvento necesita el código crudo para las
// estadísticas por gestor, sin la restricción de origen 'publico' que sí
// aplica a getGestorActivoInfo (esa es solo para el precio del mensaje).
export const getGestorActivoCodigo = () => {
  // En el dispositivo del admin se ignora (y se borra) cualquier gestor que haya quedado marcado de una prueba.
  if (esDispositivoAdmin()) { limpiarGestorActivo(); return null; }
  const codigo = localStorage.getItem(GESTOR_KEY);
  if (!codigo) return null;
  const expira = parseInt(localStorage.getItem(GESTOR_EXPIRA_KEY) || '0', 10);
  if (Date.now() > expira) {
    limpiarGestorActivo();
    return null;
  }
  return codigo;
};

// Fire-and-forget: consulta el RPC público y cachea nombre + descuento
// actual. Se llama una vez al montar el flujo público -- si falla (sin
// red en ese instante) simplemente no hay descuento en el mensaje de esa
// visita, no rompe nada.
export const sincronizarGestorActivo = async () => {
  const codigo = getGestorActivoCodigo();
  if (!codigo) return;
  try {
    const { data, error } = await supabase.rpc('buscar_gestor_por_codigo', { p_codigo: codigo });
    const fila = !error && data && data[0];
    if (fila) {
      localStorage.setItem(GESTOR_CACHE_KEY, JSON.stringify({
        nombre: fila.nombre, descuentoActualUsd: parseFloat(fila.descuento_actual_usd) || 0,
      }));
    }
  } catch {}
};

// Igual que getCanalDescuentoInfo: solo puede aplicar en el flujo público,
// nunca logueado (mismo motivo -- es una marca por dispositivo, no por
// sesión de navegación).
export const getGestorActivoInfo = (origen) => {
  if (origen !== 'publico') return null;
  const codigo = getGestorActivoCodigo();
  if (!codigo) return null;
  try {
    const cache = JSON.parse(localStorage.getItem(GESTOR_CACHE_KEY) || 'null');
    return cache ? { codigo, ...cache } : null;
  } catch {
    return null;
  }
};

// Quien está activo en este dispositivo para este origen -- el canal de
// Luis Omar (regla costo+margen) o un gestor (regla precio normal menos su
// descuento). Abrir un link ?j= o ?g= limpia el otro marcador (ver App.jsx),
// así que en condiciones normales nunca hay dos marcados al mismo tiempo --
// esta prioridad del canal es solo un respaldo defensivo por si quedó algo
// de localStorage de antes de ese cambio.
export const getRemitenteActivo = (origen) => {
  const canal = getCanalDescuentoInfo(origen);
  if (canal) return { tipo: 'canal', nombre: canal.nombre };
  const gestor = getGestorActivoInfo(origen);
  if (gestor) return { tipo: 'gestor', nombre: gestor.nombre, descuentoActualUsd: gestor.descuentoActualUsd };
  return null;
};

// Arma el saludo del mensaje de WhatsApp -- con canal o gestor activo,
// agrega "vengo de parte de [nombre]" de forma natural, sin decir nada de
// descuentos ni comisiones. accionCapitalizada empieza con mayúscula
// (ej. "Quiero cotizar este vuelo:") porque puede ir después de un punto.
// Sin canal ni gestor, la visita pública (link propio de Yoedis) igual se
// marca con "vengo del plan de vuelos" para distinguirla de quien escribe
// desde Facebook u otro lado sin haber pasado por esta pantalla. La versión
// logueada ("autenticado") no lleva marca: ahí el cliente ya tiene cuenta.
export const construirSaludoWhatsapp = (accionCapitalizada, origen) => {
  const info = getRemitenteActivo(origen);
  if (info) return `Hola, vengo de parte de ${info.nombre}. ${accionCapitalizada}`;
  if (origen === 'publico') return `Hola, vengo del plan de vuelos. ${accionCapitalizada}`;
  return `Hola, ${accionCapitalizada.charAt(0).toLowerCase()}${accionCapitalizada.slice(1)}`;
};

// Precio Zelle para el MENSAJE de WhatsApp (nunca para lo que se ve en
// pantalla): con canal (Luis Omar), el precio es el mismo que ve cualquier
// visita -- el canal ya no tiene descuento propio, solo cambia el saludo.
// Con gestor, se resta su descuento actual del precio que esté activo en
// ese momento (normal, escalonado o precio propio, lo que ya estuviera
// calculado). El gestor siempre se lleva su descuento completo aunque eso
// baje el precio del cliente por debajo del piso normal de esa ruta: esa
// fecha se bajó por decisión propia de Yoedis, no es responsabilidad del
// gestor, y es un cliente que de otra forma no habría llegado. Sin nadie
// activo, devuelve el precio tal cual se lo pasaron.
export const calcPrecioZelleMensaje = (precioZelleBase, precioBaseUsd, origen) => {
  const info = getRemitenteActivo(origen);
  if (!info || info.tipo === 'canal') return precioZelleBase;
  return Math.round(Math.max(0, precioZelleBase - (info.descuentoActualUsd || 0)));
};

// ── Gestores: lectura y escritura para admin y para el propio gestor ────
export const getGestores = async () => {
  const [{ data: gestores, error: e1 }, { data: movimientos, error: e2 }] = await Promise.all([
    supabase.from('vuelos_gestores').select('*, profiles(name, email)').order('created_at', { ascending: false }),
    supabase.from('vuelos_gestor_movimientos').select('*').order('created_at', { ascending: false }),
  ]);
  if (e1 || !gestores) return [];
  return gestores.map((g) => {
    const propios = (movimientos || []).filter((m) => m.gestor_id === g.id);
    const ganado = propios.filter((m) => m.tipo === 'comision').reduce((a, m) => a + parseFloat(m.monto_usd), 0);
    const pagado = propios.filter((m) => m.tipo === 'pago').reduce((a, m) => a + parseFloat(m.monto_usd), 0);
    return { ...g, movimientos: propios, credito: ganado - pagado };
  });
};

export const crearGestor = async (profileId, codigo, nombre, comisionMax) => {
  const { data, error } = await supabase.rpc('crear_gestor_vuelos', {
    p_profile_id: profileId, p_codigo: codigo, p_nombre: nombre, p_comision_max: parseFloat(comisionMax) || 20,
  });
  if (error) throw error;
  return data;
};

export const actualizarGestor = async (gestorId, comisionMax, activo) => {
  const { error } = await supabase.rpc('actualizar_gestor_vuelos', {
    p_gestor_id: gestorId, p_comision_max: parseFloat(comisionMax), p_activo: activo,
  });
  if (error) throw error;
};

// Cuenta de prueba (ej. "Yoedis prueba"): su tráfico sigue funcionando
// igual, pero se excluye de las estadísticas generales de Vuelos Guyana.
export const setGestorEsPrueba = async (gestorId, esPrueba) => {
  const { error } = await supabase.rpc('set_gestor_es_prueba', { p_gestor_id: gestorId, p_es_prueba: esPrueba });
  if (error) throw error;
};

// Borra el gestor del todo (su link deja de existir) -- a diferencia de
// desactivarlo, esto también borra su ledger de comisiones/pagos.
export const eliminarGestor = async (gestorId) => {
  const { error } = await supabase.rpc('eliminar_gestor_vuelos', { p_gestor_id: gestorId });
  if (error) throw error;
};

export const registrarComisionGestor = async (gestorId, monto, nota) => {
  const { error } = await supabase.rpc('registrar_comision_gestor', {
    p_gestor_id: gestorId, p_monto: parseFloat(monto), p_nota: nota || null,
  });
  if (error) throw error;
};

export const registrarPagoGestor = async (gestorId, monto, nota) => {
  const { error } = await supabase.rpc('registrar_pago_gestor', {
    p_gestor_id: gestorId, p_monto: parseFloat(monto), p_nota: nota || null,
  });
  if (error) throw error;
};

// Busca un perfil por email exacto -- para que el admin arme el gestor
// buscando por el correo con el que esa persona ya se registró en la app,
// sin tener que copiar UUIDs a mano.
export const buscarPerfilPorEmail = async (email) => {
  const { data, error } = await supabase.from('profiles').select('id, name, email')
    .ilike('email', email.trim()).limit(1);
  if (error || !data || data.length === 0) return null;
  return data[0];
};

// Para la pantalla del propio gestor (logueado con su cuenta).
export const getGestorPropio = async (uid) => {
  const { data, error } = await supabase.from('vuelos_gestores').select('*').eq('profile_id', uid).maybeSingle();
  if (error || !data) return null;
  const { data: movimientos } = await supabase.from('vuelos_gestor_movimientos')
    .select('*').eq('gestor_id', data.id).order('created_at', { ascending: false });
  const ganado = (movimientos || []).filter((m) => m.tipo === 'comision').reduce((a, m) => a + parseFloat(m.monto_usd), 0);
  const pagado = (movimientos || []).filter((m) => m.tipo === 'pago').reduce((a, m) => a + parseFloat(m.monto_usd), 0);
  return { ...data, movimientos: movimientos || [], credito: ganado - pagado };
};

export const setDescuentoGestor = async (monto) => {
  const { error } = await supabase.rpc('gestor_set_descuento', { p_descuento: parseFloat(monto) || 0 });
  if (error) throw error;
};

// ── Leads del gestor: transparencia sobre quién le escribió por WhatsApp ──
// Cada clic en el link del gestor que abre WhatsApp queda como un "lead"
// con estado (pendiente/cerrado/pagado) -- sin datos del cliente, eso nunca
// se captura (el clic solo abre WhatsApp fuera de la app).
export const getMisLeadsGestor = async () => {
  const { data, error } = await supabase.rpc('gestor_mis_leads');
  if (error || !data) return [];
  return data;
};

// Admin: cierra una venta traída por un lead puntual (clic con gestor_codigo)
// -- acredita la comisión al gestor y marca el evento como 'cerrado'.
export const cerrarLeadGestor = async (eventoId, monto, nota) => {
  const { error } = await supabase.rpc('admin_cerrar_lead_gestor', {
    p_evento_id: eventoId, p_monto: parseFloat(monto), p_nota: nota || null,
  });
  if (error) throw error;
};

// Admin: confirma que la persona detrás de un toque en WhatsApp SÍ le
// escribió (toque -> pendiente), o lo devuelve a toque si fue un error. Un
// toque solo dice que alguien abrió WhatsApp desde el link del gestor; no
// hay forma de saber desde la app si llegó a enviar el mensaje.
export const marcarLeadEscribio = async (eventoId, escribio) => {
  const { error } = await supabase.rpc('admin_marcar_escribio_lead_gestor', {
    p_evento_id: eventoId, p_escribio: escribio,
  });
  if (error) throw error;
};

// Admin: deshace un cierre por error (monto equivocado, lead incorrecto) --
// solo mientras siga sin pagar. Borra también el movimiento de comisión.
export const revertirCierreLeadGestor = async (eventoId) => {
  const { error } = await supabase.rpc('admin_revertir_cierre_lead_gestor', { p_evento_id: eventoId });
  if (error) throw error;
};

// Admin: confirma que YA pagó (transferencia real hecha) una venta ya
// cerrada -- paso separado a propósito de cerrarLeadGestor.
export const pagarLeadGestor = async (eventoId) => {
  const { error } = await supabase.rpc('admin_pagar_lead_gestor', { p_evento_id: eventoId });
  if (error) throw error;
};
